import { ReactNode, useEffect, useState } from "react";

import { Classes, Icon, Spinner } from "@blueprintjs/core";
import { PaymentRequest } from "@stripe/stripe-js";

import affirm from "@/../../hypetrain/static/img/affirm.svg";
import { formatMoney } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { CreditIcon } from "@/components/pieces/CreditIcon";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { BRAND_PURPLE, SITE_NAME } from "@/css/constants";
import { StripePaymentMethodType } from "@/graphql";
import { PendingOrder } from "@/types/products";
import { parseDecimal } from "@/utils/string";

import { CardSelector } from "./CardSelector";
import { CreditsConfirmation } from "./CreditsConfirmation";

function AffirmConfirmation({ orderTotal }: { orderTotal: string }) {
  return (
    <Box
      className="bas round-corners"
      backgroundColor="#FFF"
      pt={24}
      pb={3}
      px={[3, 24]}
    >
      <Box fontSize={18} fontWeight={600} mb={3}>
        Pay over time with Affirm
      </Box>
      {orderTotal ? (
        <p>
          <b>
            As low as {formatMoney({ cashflow: parseDecimal(orderTotal) / 12 })}
            /month for 12 months at 0% APR.
          </b>
        </p>
      ) : null}
      <p>
        {SITE_NAME} has partnered with Affirm to offer financing. Click the
        button below to get started.
      </p>
    </Box>
  );
}

interface PaymentMethodSelectorProps {
  loading: boolean;
  paymentMethods: StripePaymentMethodType[];
  paymentRequest?: PaymentRequest;
  selectedMethodId?: string;
  setSelectedMethodId: (id: string) => void;
  ledgerAccountId?: string;
  setLedgerAccountId: (id: string) => void;
  billingName: string;
  setBillingName: (name: string) => void;
  orderTotal: string;
  orderCreditTotal?: string | null;
  user?: PendingOrder["user"];
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  allowBuyCredits: boolean;
  selectedType: string;
  onChangePaymentType: (type: string) => void;
}

export function PaymentMethodSelector({
  loading,
  paymentMethods,
  paymentRequest,
  selectedMethodId,
  setSelectedMethodId,
  ledgerAccountId,
  setLedgerAccountId,
  billingName,
  setBillingName,
  orderTotal,
  orderCreditTotal,
  user,
  handleSubmit,
  allowBuyCredits = true,
  selectedType,
  onChangePaymentType,
}: PaymentMethodSelectorProps) {
  const [defaultHandled, setDefaultHandled] = useState(false);
  const orderCreditTotalNumber = parseDecimal(orderCreditTotal);

  useEffect(() => {
    if (user && !defaultHandled) {
      const availableCredits = parseDecimal(user.availableCredits);
      if (
        !allowBuyCredits ||
        (availableCredits >= orderCreditTotalNumber &&
          orderCreditTotalNumber > 0)
      ) {
        onChangePaymentType("CREDITS");
        setSelectedMethodId("CREDITS");
      }
      setDefaultHandled(true);
    }
  }, [user, allowBuyCredits]);

  function MethodButton({
    onClick,
    selected,
    children,
  }: {
    onClick: () => void;
    selected: boolean;
    children: ReactNode;
  }) {
    return (
      <Clickable onClick={onClick}>
        <Box
          width={[1, 1, 1 / 3]}
          className={`${Classes.CARD} ${Classes.INTERACTIVE}`}
          mx={[0, 0, 12]}
          mb={[3, 3, 0]}
          flex={["undefined", "undefined", 1]}
          alignItems="center"
          fontWeight={600}
          css={{
            border: "3px solid #e2e2e2",
            borderColor: selected ? BRAND_PURPLE : "#e2e2e2",
          }}
        >
          {children}
        </Box>
      </Clickable>
    );
  }

  if (loading) return <Spinner intent="primary" />;

  return (
    <>
      {allowBuyCredits && (
        <>
          <Box fontSize={22} fontWeight={600} mb={3}>
            Choose payment method
          </Box>
          <Flex
            flexWrap="wrap"
            justifyContent="space-between"
            mx={[0, 0, -12]}
            mt={3}
            mb={[3, 4]}
          >
            <MethodButton
              onClick={() => {
                onChangePaymentType("CARD");
                setSelectedMethodId(
                  paymentMethods.length
                    ? paymentMethods[0].stripeId || "NEW"
                    : "NEW",
                );
              }}
              selected={selectedType === "CARD"}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <div>Credit Card</div>
                <Icon
                  icon="credit-card"
                  size={28}
                  color="rgba(119, 131, 151, 0.6)"
                />
              </Flex>
            </MethodButton>
            <MethodButton
              onClick={() => {
                onChangePaymentType("AFFIRM");
                setSelectedMethodId("AFFIRM");
              }}
              selected={selectedType === "AFFIRM"}
            >
              <Flex justifyContent="space-between" alignItems="flex-end">
                <img src={affirm} style={{ height: 28 }} />
                <Box fontWeight="600" color="rgba(119, 131, 151, 0.6)">
                  Pay later
                </Box>
              </Flex>
            </MethodButton>
            {orderCreditTotalNumber > 0 ? (
              <MethodButton
                onClick={() => {
                  onChangePaymentType("CREDITS");
                  setSelectedMethodId("CREDITS");
                }}
                selected={selectedType === "CREDITS"}
              >
                <Flex justifyContent="space-between" alignItems="flex-end">
                  <div>Credits</div>
                  <CreditIcon width={28} height={28} />
                </Flex>
              </MethodButton>
            ) : null}
          </Flex>
        </>
      )}
      {selectedType === "CARD" ? (
        <CardSelector
          paymentRequest={paymentRequest}
          paymentMethods={paymentMethods}
          selectedMethodId={selectedMethodId}
          setSelectedMethodId={setSelectedMethodId}
          billingName={billingName}
          setBillingName={setBillingName}
          handleSubmit={handleSubmit}
        />
      ) : selectedType === "AFFIRM" ? (
        <AffirmConfirmation orderTotal={orderTotal} />
      ) : selectedType === "CREDITS" ? (
        <CreditsConfirmation
          ledgerAccounts={user?.ledgerAccounts || []}
          ledgerAccountId={ledgerAccountId}
          setLedgerAccountId={setLedgerAccountId}
          orderCreditTotal={orderCreditTotal}
          allowBuyCredits={allowBuyCredits}
        />
      ) : null}
    </>
  );
}
